import { createContext, useContext, useState, useEffect, useMemo } from "react";
import API from "../api/axios";
import { clearSession, getSessionToken, getSessionUser, setSession, setSessionUser } from "../utils/session";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getSessionUser());
  const [token, setToken] = useState(getSessionToken());
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const bootstrapAuth = async () => {
      const token = getSessionToken();
      if (!token) {
        setIsAuthLoading(false);
        return;
      }

      try {
        const { data } = await API.get("/users/profile");
        const profileUser = data.user || null;
        setUser(profileUser);
        if (profileUser) {
          setSessionUser(profileUser);
        }
      } catch (error) {
        clearSession();
        setToken(null);
        setUser(null);
      } finally {
        setIsAuthLoading(false);
      }
    };

    bootstrapAuth();
  }, []);

  const login = (nextUser, token) => {
    if (token) {
      setSession(token, nextUser || getSessionUser());
      setToken(token);
    }
    if (nextUser) {
      setUser(nextUser);
      setSessionUser(nextUser);
      return;
    }
    setUser((prev) => prev || { email: "authenticated@local" });
  };

  const logout = () => {
    clearSession();
    setToken(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      setUser,
      login,
      logout,
      isAuthLoading,
      isAuthenticated: Boolean(token),
    }),
    [user, token, isAuthLoading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);